// friendly-error — convert raw exceptions into user-readable Chinese strings.
//
// Per CLAUDE.md §2 hard constraint #10: end-user 主母 must NEVER see a stack
// trace. Per LLM_NATIVE_ARCHITECTURE.md §10: hard constraints are
// load-bearing — every catch block that surfaces a message to the UI must go
// through this mapper.
//
// Wording rule (Session 19AN):
//   User-visible copy uses 「开发者」(developer / operator), NOT 「陛下」, since
//   non-principal users may also see the message. Internal comments / DEVLOG
//   may keep 「陛下」 as the operator referent.
//
// Adding a new error class:
//   1. Add a case to `toFriendlyMessage` matching the new class
//   2. Pick a friendly Chinese phrasing (≤ 80 chars)
//   3. Optionally surface a hint pointing at recovery action (retry / reload /
//      contact developer)

import { IngestApiError } from '../api/ingest.js';
import {
  INGEST_ERROR_DESCRIPTIONS,
  IngestPipelineError,
  type IngestErrorCode,
} from '@examforge/shared/errors';

export interface FriendlyError {
  /** Short Chinese phrase suitable for inline status. */
  message: string;
  /** Optional secondary hint (e.g. "请重试" / "请刷新页面"). */
  hint?: string;
  /** Stable enum tag — UI can branch on this for icon / color. */
  kind:
    | 'aborted'
    | 'network'
    | 'llm_call'
    | 'llm_output'
    | 'ingest_api'
    | 'pipeline'
    | 'schema'
    | 'storage'
    | 'unknown';
  /** 19BD-PROD: stable IngestErrorCode when err was an IngestPipelineError.
   *  UI uses this to render `[CODE] 描述`. Admin already has it from D1. */
  code?: IngestErrorCode;
}

const RETRY_HINT = '请稍后重试，若多次失败请联系开发者。';
const RELOAD_HINT = '请刷新页面后重试。';

/** Convert any thrown value into a UI-safe message + recovery hint. */
export function toFriendlyError(err: unknown): FriendlyError {
  // ── Aborted (user navigated away / cancelled) ──────────────────────────
  if (err instanceof Error && err.name === 'AbortError') {
    return { kind: 'aborted', message: '已取消' };
  }

  // ── IngestPipelineError — structured pipeline failure (19BD-PROD) ──────
  // Carries stable IngestErrorCode. Per陛下: UI shows `[CODE] 一句描述`,
  // never stack traces. Admin has full forensic via D1 error_message column.
  if (err instanceof IngestPipelineError) {
    return {
      kind: 'pipeline',
      code: err.code,
      message: `[${err.code}] ${INGEST_ERROR_DESCRIPTIONS[err.code]}`,
      hint: err.code === 'BUDGET_EXCEEDED' ? '请联系开发者补充额度。' : RETRY_HINT,
    };
  }

  // ── IngestApiError — structured response from worker ───────────────────
  if (err instanceof IngestApiError) {
    const status = err.status;
    if (status === 503) {
      // Check for specific backend error codes in the response body.
      const body = (err.response as { error?: string; message?: string }) ?? {};
      if (body.error === 'workflow_not_bound') {
        return {
          kind: 'ingest_api',
          message: '生成工作流未就绪（开发环境预期行为）',
          hint: '生产环境部署后此问题将自动解决。本地开发请使用 fixture 模式。',
        };
      }
      return {
        kind: 'ingest_api',
        message: '生成服务暂时不可用（503）',
        hint: RETRY_HINT,
      };
    }
    if (status >= 500) {
      return {
        kind: 'ingest_api',
        message: '生成服务暂时不可用',
        hint: RETRY_HINT,
      };
    }
    if (status === 429) {
      return {
        kind: 'ingest_api',
        message: '请求过于频繁，已被限流',
        hint: '请等候 1-2 分钟后再试。',
      };
    }
    if (status === 413) {
      return {
        kind: 'ingest_api',
        message: '上传内容过大，超出生成上限',
        hint: '请精简源材料后重试。',
      };
    }
    if (status === 401 || status === 403) {
      return {
        kind: 'ingest_api',
        message: '生成服务认证失败',
        hint: '请联系开发者检查 API 配置。',
      };
    }
    return {
      kind: 'ingest_api',
      message: '课程生成失败',
      hint: RETRY_HINT,
    };
  }

  // ── Network / fetch failures ────────────────────────────────────────────
  // Browser surfaces these as TypeError("Failed to fetch") or NetworkError.
  if (err instanceof TypeError && /fetch|network/i.test(err.message)) {
    return {
      kind: 'network',
      message: '网络连接异常',
      hint: '请检查网络后重试。',
    };
  }

  // ── LLM-specific errors (worker-side bubbled up) ───────────────────────
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (err.name === 'LlmCallError' || /llm|provider|deepseek|glm/i.test(err.message)) {
      return {
        kind: 'llm_call',
        message: 'AI 模型调用失败',
        hint: RETRY_HINT,
      };
    }
    if (err.name === 'LlmOutputValidationError' || msg.includes('schema')) {
      return {
        kind: 'llm_output',
        message: 'AI 输出格式异常',
        hint: '请重新生成；如反复失败请联系开发者。',
      };
    }
    // IDB / storage failures
    if (
      err.name === 'QuotaExceededError'
      || msg.includes('indexeddb')
      || msg.includes('quota')
    ) {
      return {
        kind: 'storage',
        message: '本地存储空间不足',
        hint: '请在设置中导出并清理旧课程后重试。',
      };
    }
  }

  // ── Generic fallback ────────────────────────────────────────────────────
  return {
    kind: 'unknown',
    message: '出了点问题',
    hint: RELOAD_HINT,
  };
}

/** Convenience: format a FriendlyError as a single string for setError(...).
 *  UI components that want the structured form should use toFriendlyError directly. */
export function toFriendlyMessage(err: unknown): string {
  const fe = toFriendlyError(err);
  return fe.hint ? `${fe.message} · ${fe.hint}` : fe.message;
}
